<template>
  <section
    ref="browserSection"
    class="section article-browser-section"
    :data-route-scroll-path="route.path"
    :data-route-scroll-navigation-id="routeScrollInstanceId"
    :data-route-scroll-ready="isRouteScrollReady ? 'true' : 'false'"
  >
    <div v-if="sectionNav.length > 1" class="article-browser-tabbar">
      <div class="page-container page-container--narrow">
        <nav ref="categoryNav" class="article-tab-nav" aria-label="资讯栏目分类">
          <nuxt-link
            v-for="item in sectionNav"
            :key="item.code"
            :to="item.path"
            class="article-tab-item"
            :class="{ 'is-active': item.code === selectedCategoryCode }"
            :aria-current="item.code === selectedCategoryCode ? 'page' : undefined"
          >
            {{ item.name }}
          </nuxt-link>
          <span class="article-tab-indicator" aria-hidden="true"></span>
        </nav>
      </div>
    </div>

    <div class="page-container page-container--narrow">
      <div class="article-browser-content">
        <div v-if="showListLoading" class="article-state" role="status">
          <i class="i-lucide-loader-circle is-spinning" aria-hidden="true"></i>
          <span>正在加载{{ displayLabel }}内容...</span>
        </div>

        <div v-else-if="hasError" class="article-state is-error" role="alert">
          <i class="i-lucide-circle-alert" aria-hidden="true"></i>
          <strong>{{ displayLabel }}暂时无法加载</strong>
          <p>请稍后重试，接口恢复后已发布内容将在此展示。</p>
          <NiceButton type="button" @click="retry">重新加载</NiceButton>
        </div>

        <template v-else-if="visibleArticles.length > 0">
          <div class="article-list" role="list">
            <article-list-item
              v-for="article in visibleArticles"
              :key="article.id"
              :article="article"
              role="listitem"
            />
          </div>

          <div
            ref="paginationReveal"
            class="article-pagination"
            :class="{
              'is-reveal-ready': paginationRevealReady,
              'is-revealed': paginationRevealed,
            }"
          >
            <site-pagination
              :page="page"
              :page-count="pageCount"
              :pending="pending"
              :label="`${displayLabel}分页`"
              @update:page="handlePageChange"
            />
          </div>
        </template>

        <div v-else class="article-state is-empty" role="status">
          <i class="i-lucide-file-text" aria-hidden="true"></i>
          <strong>当前暂无内容</strong>
          <p>相关内容发布后将在此展示。</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { ArticleItem, PageResponse } from '~/types/site-api'

  import { NiceButton } from '@nice/nice-ui'

  import { findArticleCategory, getArticleCategoryRoute } from '~/utils/content'
  import { getRouteScrollNavigationId, isScrollRestorationNavigation } from '~/utils/route-scroll'

  interface Props {
    category: string
    pageSize?: number
  }

  defineOptions({
    name: 'ArticleBrowser',
  })

  const props = withDefaults(defineProps<Props>(), {
    pageSize: 9,
  })

  const route = useRoute()
  const { getArticles } = useSiteApi()
  const browserSection = useTemplateRef<HTMLElement>('browserSection')
  const categoryNav = useTemplateRef<HTMLElement>('categoryNav')
  const routeScrollInstanceId = getRouteScrollNavigationId()
  const page = shallowRef(1)
  const isBrowserMounted = shallowRef(false)
  const categoryScrollRequest = shallowRef(0)
  const loadedCategoryCode = shallowRef('')
  const hasResolvedCategory = shallowRef(false)
  let categoryScrollSequence = 0

  const {
    targetRef: paginationReveal,
    isReady: paginationRevealReady,
    isVisible: paginationRevealed,
  } = useScrollReveal(0.1)

  const {
    data: categoryResponse,
    pending: categoriesPending,
    error: categoryError,
    refresh: refreshCategories,
  } = useArticleCategories()

  const categoryTree = computed(() => categoryResponse.value?.data ?? [])
  const categoryMatch = computed(() => findArticleCategory(categoryTree.value, props.category))
  const rootCategory = computed(() => categoryMatch.value?.root)
  const selectedRootCategoryCode = computed(() => rootCategory.value?.code ?? '')
  const selectedCategoryCode = computed(() => categoryMatch.value?.category.code ?? '')
  const displayLabel = computed(() => categoryMatch.value?.category.name ?? '资讯')
  /** 栏目导航 Tab：当前一级栏目下的二级分类。 */
  const sectionNav = computed(() => {
    const root = rootCategory.value
    if (!root) return []
    return root.children.map((child) => ({
      code: child.code,
      name: child.name,
      path: getArticleCategoryRoute(child).path,
    }))
  })
  /** 读取当前分类的全部分页，保证前端分页时内容完整。 */
  async function fetchCategoryArticles(category: string, signal?: AbortSignal) {
    const query = {
      category,
      page: 1,
      page_limit: 10,
    }
    const firstPage = await getArticles(query, signal)
    const remainingPages = Array.from(
      { length: Math.max(0, firstPage.meta.last_page - 1) },
      (_, index) => index + 2
    )
    if (remainingPages.length === 0) return firstPage.data

    const rest = await Promise.all(
      remainingPages.map((currentPage) => getArticles({ ...query, page: currentPage }, signal))
    )
    return [firstPage, ...rest].flatMap((response: PageResponse<ArticleItem>) => response.data)
  }

  /** 按 URL 指定分类读取已发布文章。 */
  async function loadArticles(signal?: AbortSignal) {
    const category = selectedCategoryCode.value
    loadedCategoryCode.value = ''
    if (!category) return []

    try {
      const articles = await fetchCategoryArticles(category, signal)
      if (!signal?.aborted) loadedCategoryCode.value = category
      return articles.sort((left, right) => {
        const dateOrder = right.publish_date.localeCompare(left.publish_date)
        return dateOrder || right.id.localeCompare(left.id)
      })
    } catch (error) {
      if (!signal?.aborted) loadedCategoryCode.value = category
      throw error
    }
  }

  const {
    data: articleData,
    pending: articlesPending,
    error: articleError,
    status: articleStatus,
    refresh: refreshArticles,
  } = await useAsyncData<ArticleItem[]>(
    `article-browser-${props.category}`,
    (_nuxtApp, { signal }) => loadArticles(signal),
    {
      watch: [selectedCategoryCode],
    }
  )

  if (
    import.meta.client &&
    articleData.value !== undefined &&
    (articleStatus.value === 'success' || articleStatus.value === 'error')
  ) {
    loadedCategoryCode.value = props.category
  }

  const articles = computed(() => articleData.value ?? [])
  const pending = computed(() => categoriesPending.value || articlesPending.value)
  const hasError = computed(() => Boolean(categoryError.value || articleError.value))
  const hasLoadedCurrentCategory = computed(
    () =>
      Boolean(selectedCategoryCode.value) && loadedCategoryCode.value === selectedCategoryCode.value
  )
  const showListLoading = computed(() => !categoryError.value && !hasLoadedCurrentCategory.value)
  const isRouteScrollReady = computed(() => {
    if (pending.value) return false
    if (categoryError.value) return true
    return (
      Boolean(selectedCategoryCode.value) &&
      loadedCategoryCode.value === selectedCategoryCode.value &&
      (articleStatus.value === 'success' || articleStatus.value === 'error')
    )
  })
  const pageCount = computed(() => Math.max(1, Math.ceil(articles.value.length / props.pageSize)))
  const visibleArticles = computed(() => {
    if (!hasLoadedCurrentCategory.value) return []
    const start = (page.value - 1) * props.pageSize
    return articles.value.slice(start, start + props.pageSize)
  })

  /** 将列表起点带回视口。 */
  function scrollToBrowserSection(behavior?: ScrollBehavior) {
    if (!import.meta.client) return
    browserSection.value?.scrollIntoView({
      behavior:
        behavior ??
        (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'),
      block: 'start',
    })
  }

  /** 将分类切换后的页面回到原有的顶部位置。 */
  function scrollToPageTop() {
    if (!import.meta.client) return
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({
      left: 0,
      top: 0,
      behavior: reduceMotion ? 'auto' : 'smooth',
    })
  }

  watch(
    () => props.category,
    () => {
      page.value = 1
    }
  )

  watch(pageCount, (count) => {
    if (page.value > count) page.value = count
  })

  watch(
    [selectedCategoryCode, selectedRootCategoryCode],
    ([nextCategory, nextRootCategory], [previousCategory, previousRootCategory]) => {
      if (nextCategory === previousCategory) return

      loadedCategoryCode.value = ''

      const isSameRootCategory =
        Boolean(nextRootCategory) && nextRootCategory === previousRootCategory
      if (isSameRootCategory) {
        // 同一一级分类下的 Tab 切换保持当前页面滚动位置，并取消旧的回顶请求。
        categoryScrollRequest.value = 0
      } else if (
        isBrowserMounted.value &&
        hasResolvedCategory.value &&
        !isScrollRestorationNavigation()
      ) {
        categoryScrollRequest.value = ++categoryScrollSequence
      }

      if (nextCategory) hasResolvedCategory.value = true
    }
  )

  watch(
    [articlesPending, articles],
    async ([isPending], _oldValue, onCleanup) => {
      const requestId = categoryScrollRequest.value
      if (!requestId || isPending) return

      let cancelled = false
      onCleanup(() => {
        cancelled = true
      })

      await nextTick()
      if (
        cancelled ||
        requestId !== categoryScrollRequest.value ||
        articlesPending.value ||
        !isBrowserMounted.value
      )
        return

      categoryScrollRequest.value = 0
      scrollToPageTop()
    },
    { flush: 'post' }
  )

  watch(selectedCategoryCode, positionSelectedCategory, { flush: 'post' })
  watch(sectionNav, positionTabIndicator, { flush: 'post' })

  /** 重新加载分类与文章数据。 */
  async function retry() {
    await refreshCategories()
    await refreshArticles()
  }

  /** 将当前激活的 Tab 滚动到可视区域居中位置。 */
  async function positionSelectedCategory() {
    if (!import.meta.client) return
    await nextTick()

    const nav = categoryNav.value
    const activeItem = nav?.querySelector<HTMLElement>('.article-tab-item.is-active')
    if (!nav || !activeItem) return

    const navRect = nav.getBoundingClientRect()
    const activeRect = activeItem.getBoundingClientRect()
    const centeredOffset = (navRect.width - activeRect.width) / 2
    nav.scrollTo({
      left: Math.max(0, nav.scrollLeft + activeRect.left - navRect.left - centeredOffset),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
    positionTabIndicator()
  }

  /** 将激活态下划线滑动到当前 Tab 位置。 */
  function positionTabIndicator() {
    if (!import.meta.client) return
    const nav = categoryNav.value
    const indicator = nav?.querySelector<HTMLElement>('.article-tab-indicator')
    const activeItem = nav?.querySelector<HTMLElement>('.article-tab-item.is-active')
    if (!nav || !indicator) return
    if (!activeItem) {
      indicator.style.width = '0'
      indicator.style.transform = ''
      return
    }
    const targetWidth = `${activeItem.offsetWidth}px`
    const targetTransform = `translateX(${activeItem.offsetLeft}px)`
    // 首次定位（无历史 transform）时禁用过渡，直接到位，避免从页面左侧滑入；后续切换正常滑动转移。
    if (!indicator.style.transform) {
      indicator.style.transition = 'none'
      indicator.style.width = targetWidth
      indicator.style.transform = targetTransform
      void indicator.offsetWidth
      indicator.style.transition = ''
      return
    }
    indicator.style.width = targetWidth
    indicator.style.transform = targetTransform
  }

  /** 切换分页并将列表起点带回视口。 */
  function handlePageChange(nextPage: number) {
    // 在列表切片前完成定位，避免新页高度变化时视口先被浏览器校正。
    scrollToBrowserSection('auto')
    page.value = nextPage
  }

  onMounted(() => {
    isBrowserMounted.value = true
    if (!categoriesPending.value) hasResolvedCategory.value = true
    positionSelectedCategory()
    window.addEventListener('resize', positionTabIndicator)
  })

  onUnmounted(() => {
    isBrowserMounted.value = false
    window.removeEventListener('resize', positionTabIndicator)
  })
</script>

<style scoped lang="scss">
  @keyframes article-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (hover: hover) and (pointer: fine) {
    .article-tab-item:hover {
      color: var(--accent);
    }

    .article-state button:hover {
      background: var(--primary-strong);
      border-color: var(--primary-strong);
    }
  }

  @media (hover: none) {
    .article-tab-item:active {
      color: var(--accent);
    }

    .article-state button:active {
      background: var(--primary-strong);
      border-color: var(--primary-strong);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .is-spinning {
      animation: none;
    }
  }

  @include lt-sm {
    .article-browser-section {
      padding: 0 0 48px;
      scroll-margin-top: 72px;
    }

    .article-tab-nav {
      gap: 22px;
      padding-top: 12px;
    }

    .article-tab-item {
      padding-bottom: 12px;
      font-size: 13px;
    }

    .article-browser-content {
      padding-block: 20px 8px;
    }

    .article-state {
      min-height: 220px;
      padding: 32px 20px;
    }
  }

  .article-browser-section {
    position: relative;
    z-index: 2;
    padding-top: 0;
    scroll-margin-top: 92px;
    background: var(--surface-soft);
  }

  /* 通栏 Tab 栏 */
  .article-browser-tabbar {
    background: var(--surface);
    border-bottom: 1px solid var(--line);
  }

  .article-tab-nav {
    position: relative;
    display: flex;
    gap: 30px;
    justify-content: safe center;
    min-width: 0;
    padding-top: 16px;
    overflow-x: auto;
    overscroll-behavior-inline: contain;
    scroll-snap-type: inline proximity;
    scrollbar-width: none;

    /* border-bottom: 1px solid var(--line); */

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .article-tab-item {
    position: relative;
    flex-shrink: 0;
    padding: 0 2px 16px;
    font-size: 14px;
    line-height: 1.4;
    color: var(--muted);
    white-space: nowrap;
    cursor: pointer;
    scroll-snap-align: start;
    transition: color 0.2s ease;
  }

  .article-tab-item.is-active {
    font-weight: 600;
    color: var(--accent);
  }

  .article-tab-indicator {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 3px;
    background: var(--accent);
    transition:
      transform 280ms var(--ease),
      width 280ms var(--ease);
  }

  /* 文章列表区 */
  .article-browser-content {
    min-width: 0;
    padding-bottom: clamp(40px, 4vw, 56px);
  }

  .article-pagination.is-reveal-ready:not(.is-revealed) {
    opacity: 0;
    transform: translateY(24px);
  }

  .article-pagination.is-reveal-ready.is-revealed {
    opacity: 1;
    transform: none;
    transition:
      opacity 520ms var(--ease),
      transform 520ms var(--ease);
  }

  .article-list {
    display: grid;
    min-width: 0;
  }

  .article-state {
    display: grid;
    gap: 10px;
    place-content: center;
    justify-items: center;
    min-height: 280px;
    padding: 40px 24px;
    color: var(--muted);
    text-align: center;
    background: var(--surface-soft);
    border: 1px dashed var(--line);
    border-radius: var(--radius-md);
  }

  .article-state.is-empty {
    background: transparent;
  }

  .article-state > i {
    width: 34px;
    height: 34px;
    color: var(--accent);
  }

  .article-state strong {
    font-size: 18px;
    color: var(--ink);
  }

  .article-state p {
    margin: 0;
  }

  .article-state button {
    min-height: 46px;
    padding: 9px 18px;
    margin-top: 8px;
    font-weight: 600;
    color: var(--white);
    cursor: pointer;
    background: var(--accent);
    border: 1px solid var(--accent);
    border-radius: var(--radius-md);
  }

  .is-spinning {
    animation: article-spin 900ms linear infinite;
  }
</style>
